import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { Company } from '@interfaces/common/company';
import {
  CharterActRefDto,
  CharterDataRoomPendingDto,
  CompanyCorporateCharterDto,
  CorporateCharterActSummaryDto,
  CorporateCharterReprocessResultDto,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { DataRoomDocumentLinkService } from '@services/helpers/data-room-document-link/data-room-document-link.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { CompanyService } from '@services/http/company.service';
import { CorporateCharterHttpService } from '@services/http/corporate-charter/corporate-charter-http.service';
import { CHARTER_POLL_MS } from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { Subscription, catchError, finalize, forkJoin, of, timer } from 'rxjs';

/**
 * Estado da tela Societário da empresa (PBI 3260): carrega o vigente e os atos, atualiza sozinha enquanto algum ato
 * está sendo lido, reprocessa os contratos da empresa e abre um documento no Data Room. Provido pelo componente da
 * página (uma instância por tela).
 */
@Injectable()
export class CompanyCorporateCharterPageState {
  private readonly http = inject(CorporateCharterHttpService);
  private readonly documentLink = inject(DataRoomDocumentLinkService);
  private readonly companies = inject(CompanyService);
  private readonly permissions = inject(PermissionsService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly formErrorHandler = inject(FormErrorHandlerService);
  private readonly dates = inject(DateFormatterService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly companyId = signal<string | null>(null);
  readonly company = signal<Company | null>(null);
  readonly charter = signal<CompanyCorporateCharterDto | null>(null);
  readonly acts = signal<CorporateCharterActSummaryDto[]>([]);
  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly reprocessing = signal(false);
  /**
   * "Processar documentos do Data Room" foi enviado e a tela espera o Cog: sem vigente, mostra o estado "Lendo" mesmo
   * antes de algum ato aparecer (a classificação vem primeiro); com vigente, o aviso troca o botão por "enviado".
   * Volta a `false` quando as releituras garantidas acabam sem nada em leitura.
   */
  readonly processingRequested = signal(false);
  readonly openingDocumentId = signal<string | null>(null);
  /** Escrita (reprocessar) exige `DATAROOM_UPDATE`; leitura já passou pelo guard com `DATAROOM_VIEW`. */
  readonly canEdit = signal(false);
  /**
   * Time interno (TENANT ou ADMIN) vê modelo, passadas e custo da leitura de cada ato; ADMIN abre a sessão do agente no
   * back office. Mesmo critério das "Sessões do Cog" do Data Room. O backend manda nulo para usuário de empresa.
   */
  readonly isInternal = signal(false);
  readonly isAdmin = signal(false);

  private pollSubscription: Subscription | null = null;
  private loadSubscription: Subscription | null = null;
  /**
   * Releituras garantidas depois de reprocessar: o ato pode demorar a aparecer como "lendo" (a fila de jobs ainda não
   * pegou o documento), e sem isto a tela pararia de se atualizar antes de o Cog começar.
   */
  private forcedPolls = 0;
  /** Releituras garantidas depois de "Processar documentos do Data Room" com documento indo para classificação. */
  private static readonly CLASSIFICATION_FORCED_POLLS = 22;
  private static readonly READING_FORCED_POLLS = 6;

  readonly companyName = computed(() => {
    const c = this.company();
    return (
      c?.displayName || c?.corporateName || this.charter()?.company.corporateName.value || null
    );
  });

  readonly companyCnpj = computed(() => {
    // O CNPJ do contrato vigente vem primeiro; sem ele (ou ilegível), vale o do cadastro da empresa.
    const candidatos = [this.charter()?.company.cnpj.value, this.company()?.cnpj];
    for (const cnpj of candidatos) {
      const digits = (cnpj ?? '').replace(/\D/g, '');
      if (digits.length === 14) return digits;
    }
    return null;
  });

  readonly processingActs = computed(() => this.acts().filter(act => act.status === 'processing'));

  /** Documentos da pasta de contrato social do Data Room que o Cog ainda não leu; `null` quando não há nenhum. */
  readonly dataRoomPending = computed<CharterDataRoomPendingDto | null>(() => {
    const pending = this.charter()?.dataRoomPending;
    return pending && pending.total > 0 ? pending : null;
  });

  /** Sem ato lido e nada sendo lido: estado vazio (com os pendentes do Data Room, quando houver). */
  readonly isEmpty = computed(
    () =>
      !this.loading() &&
      !this.loadFailed() &&
      (this.charter()?.status ?? 'empty') === 'empty' &&
      this.processingActs().length === 0 &&
      !this.processingRequested()
  );

  /** Sem vigente ainda, mas o Cog está lendo (ou acabou de receber os documentos do Data Room): "processando". */
  readonly isWaitingFirstRead = computed(
    () =>
      (this.charter()?.status ?? 'empty') === 'empty' &&
      (this.processingActs().length > 0 || this.processingRequested())
  );

  /** Ato mais recente que compõe o vigente (o "documento vigente" do cabeçalho). */
  readonly currentAct = computed<CharterActRefDto | null>(() => {
    const charter = this.charter();
    if (!charter) {
      return null;
    }
    const applied = charter.appliedActs;
    return applied.length > 0 ? applied[applied.length - 1] : charter.baseAct;
  });

  /** "7ª alteração, registrada em 12/03/2026, aplicada sobre a 5ª alteração com consolidação." */
  readonly situation = computed(() => {
    const charter = this.charter();
    const current = this.currentAct();
    if (!charter || !current) {
      return null;
    }
    const registered = current.registeredAt
      ? `, registrada em ${this.dates.formatDateInstant(current.registeredAt, true)}`
      : current.signedAt
        ? `, assinada em ${this.dates.formatDateInstant(current.signedAt, true)}`
        : '';
    const base = charter.baseAct;
    const over = base && base.actId !== current.actId ? `, aplicada sobre ${base.label}` : '';
    return `${current.label}${registered}${over}.`;
  });

  /** Quantos saíram do quadro ou da administração (contador da aba). */
  readonly formerCount = computed(() => {
    const charter = this.charter();
    return (
      (charter?.formerHolders?.length ?? 0) +
      (charter?.governance.formerAdministrators?.length ?? 0)
    );
  });

  /** Atos na linha do tempo (contador da aba): a união de `timeline` e `acts`. */
  readonly actsCount = computed(() => {
    const ids = new Set(this.acts().map(act => act.actId));
    for (const entry of this.charter()?.timeline ?? []) {
      ids.add(entry.actId);
    }
    return ids.size;
  });

  readonly statusBadge = computed(() => {
    const charter = this.charter();
    switch (charter?.status) {
      case 'current':
        return { text: 'Conferido', tone: 'ok' as const };
      case 'needs-review': {
        const count = charter.pendingReviewCount;
        return {
          text: count === 1 ? '1 ato para revisar' : `${count} atos para revisar`,
          tone: 'warning' as const,
        };
      }
      case 'incomplete':
        return { text: 'Incompleto: faltam atos', tone: 'warning' as const };
      default:
        return null;
    }
  });

  constructor() {
    this.permissions
      .hasPermission(Permissions.DataRoom.UPDATE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(allowed => this.canEdit.set(allowed));
    this.permissions
      .hasRole([Roles.TENANT, Roles.ADMIN])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(internal => this.isInternal.set(internal));
    this.permissions
      .hasRole(Roles.ADMIN)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(admin => this.isAdmin.set(admin));
    this.destroyRef.onDestroy(() => {
      this.pollSubscription?.unsubscribe();
      this.loadSubscription?.unsubscribe();
    });
  }

  /** Troca a empresa da tela (parâmetro `:id` da rota). */
  setCompany(companyId: string | null): void {
    if (companyId === this.companyId()) {
      return;
    }
    this.companyId.set(companyId);
    this.forcedPolls = 0;
    this.processingRequested.set(false);
    this.company.set(null);
    this.charter.set(null);
    this.acts.set([]);
    if (!companyId) {
      this.loading.set(false);
      this.loadFailed.set(true);
      return;
    }
    this.companies
      .getCompanyById(companyId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(company => {
        if (this.companyId() === companyId) {
          this.company.set(company);
        }
      });
    this.load();
  }

  /** Carrega vigente e atos. `silent` não mostra o spinner (atualização automática e depois de uma ação). */
  load(silent = false): void {
    const companyId = this.companyId();
    if (!companyId) {
      return;
    }
    this.pollSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
    if (!silent) {
      this.loading.set(true);
      this.loadFailed.set(false);
    }
    this.loadSubscription = forkJoin({
      charter: this.http.getCompanyCharter(companyId),
      acts: this.http.getCompanyActs(companyId).pipe(catchError(() => of(null))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ charter, acts }) => {
          if (this.companyId() !== companyId) {
            return;
          }
          this.charter.set(charter);
          this.acts.set(acts ?? []);
          this.loading.set(false);
          this.loadFailed.set(!charter);
          this.schedulePoll();
        },
        error: error => {
          this.loading.set(false);
          if (!silent) {
            this.loadFailed.set(true);
            this.formErrorHandler.showToastErrors(error);
          } else {
            this.schedulePoll();
          }
        },
      });
  }

  /** "Reprocessar contratos da empresa": por padrão só os documentos que ainda não foram lidos. */
  reprocessCompany(): void {
    const companyId = this.companyId();
    if (!companyId || this.reprocessing()) {
      return;
    }
    this.confirmation
      .confirmWithCheckbox(
        'Reprocessar contratos sociais da empresa?',
        'O Cog lê de novo os contratos sociais, alterações, estatutos e atas desta empresa no Data Room. O que uma pessoa corrigiu não é sobrescrito.',
        'Só os documentos que ainda não foram lidos',
        true
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result.confirmed) {
          return;
        }
        this.reprocessing.set(true);
        this.http
          .reprocessCompany(companyId, { onlyMissing: result.checkboxValue })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: response => {
              this.reprocessing.set(false);
              this.toastReprocessResult(response);
              this.forcedPolls = (response?.enqueued.length ?? 0) > 0 ? 6 : 0;
              this.load(true);
            },
            error: error => {
              this.reprocessing.set(false);
              this.formErrorHandler.showToastErrors(error);
            },
          });
      });
  }

  /**
   * "Processar documentos do Data Room": um clique, sem confirmação (regra das ações de IA). Os documentos sem tipo da
   * pasta de contrato social vão para a classificação e os já classificados para a leitura; a tela segue no "Lendo".
   */
  processDataRoomDocuments(): void {
    const companyId = this.companyId();
    if (!companyId || this.reprocessing()) {
      return;
    }
    this.reprocessing.set(true);
    this.http
      .reprocessCompany(companyId, { onlyMissing: true, includeUnclassified: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.reprocessing.set(false);
          const classification = response?.classificationQueued ?? 0;
          const reading = response?.enqueued.length ?? 0;
          this.toastDataRoomResult(response);
          this.forcedPolls =
            classification > 0
              ? CompanyCorporateCharterPageState.CLASSIFICATION_FORCED_POLLS
              : reading > 0
                ? CompanyCorporateCharterPageState.READING_FORCED_POLLS
                : 0;
          this.processingRequested.set(classification + reading > 0);
          this.load(true);
        },
        error: error => {
          this.reprocessing.set(false);
          this.formErrorHandler.showToastErrors(error);
        },
      });
  }

  /**
   * Abre o documento no Data Room pelo link direto (`?doc=&panel=details`): a pasta dele, com o painel de detalhes
   * aberto. A pasta sai do detalhe do documento (`DataRoomDocumentLinkService`), que também avisa quando ele não existe
   * mais ou a pessoa não tem acesso.
   */
  openDocument(documentId: string): void {
    if (this.openingDocumentId()) {
      return;
    }
    this.openingDocumentId.set(documentId);
    this.documentLink
      .open(documentId, { panel: 'details', fallbackCompanyId: this.companyId() })
      .pipe(
        finalize(() => this.openingDocumentId.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /** Raiz do Data Room da empresa (CTA do estado vazio). */
  openDataRoom(): void {
    const companyId = this.companyId();
    if (companyId) {
      this.router.navigate(['/dashboard/market/data-room/company-folders', companyId], {
        queryParams: { companyId },
      });
    }
  }

  private toastReprocessResult(result: CorporateCharterReprocessResultDto | null): void {
    const enqueued = result?.enqueued.length ?? 0;
    const skipped = result?.skipped.length ?? 0;
    if (enqueued === 0) {
      const reason = result?.skipped[0]?.reason;
      this.toast.showToastWarn(
        'Nenhum documento foi enviado para leitura.',
        reason ?? 'Não há contrato social, alteração, estatuto ou ata para ler nesta empresa.'
      );
      return;
    }
    const sent = enqueued === 1 ? '1 documento enviado' : `${enqueued} documentos enviados`;
    const rest = skipped > 0 ? ` (${skipped} ficaram de fora)` : '';
    this.toast.showToastSuccess(
      `${sent} para leitura${rest}.`,
      'A tela se atualiza sozinha enquanto o Cog lê.'
    );
  }

  /** "2 documentos enviados ao Cog: 1 para classificação e 1 para leitura." */
  private toastDataRoomResult(result: CorporateCharterReprocessResultDto | null): void {
    const classification = result?.classificationQueued ?? 0;
    const reading = result?.enqueued.length ?? 0;
    const total = classification + reading;
    if (total === 0) {
      this.toast.showToastWarn(
        'Nenhum documento foi enviado ao Cog.',
        result?.skipped[0]?.reason ??
          'Os documentos da pasta de contrato social já foram lidos ou estão sendo lidos.'
      );
      return;
    }
    const parts = [
      classification > 0 ? `${classification} para classificação` : null,
      reading > 0 ? `${reading} para leitura` : null,
    ].filter(Boolean);
    const sent = total === 1 ? '1 documento enviado' : `${total} documentos enviados`;
    this.toast.showToastSuccess(
      `${sent} ao Cog: ${parts.join(' e ')}.`,
      classification > 0
        ? 'A tela se atualiza sozinha enquanto o Cog classifica e lê.'
        : 'A tela se atualiza sozinha enquanto o Cog lê.'
    );
  }

  /** Enquanto algum ato está sendo lido, relê a cada `CHARTER_POLL_MS` sem spinner. */
  private schedulePoll(): void {
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
    if (this.processingActs().length === 0) {
      if (this.forcedPolls <= 0) {
        this.processingRequested.set(false);
        return;
      }
      this.forcedPolls--;
    }
    this.pollSubscription = timer(CHARTER_POLL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(true));
  }
}
